import React, { useState, useEffect } from 'react';
import { ArrowLeft, Globe, DollarSign, TrendingUp, MapPin, Users, Home, Utensils, Car, Plane, Music, GraduationCap, Stethoscope, Wrench, Target, BarChart3, PieChart, Download, Save, Plus, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedNumberDisplay from './ui/AnimatedNumberDisplay';
import { City } from '../types/City';
import { ExpenseCategory, SelectedExpense } from '../types/ExpenseCategory';
import { cities } from '../data/cities';
import { expenseCategories } from '../data/lifestyleExpenses';
import { 
  calculateTotalExpenses, 
  calculateAffordabilityMetrics, 
  generateRecommendations,
  calculateAdjustedCost,
  calculateCertificatesForLifestyle
} from '../utils/calculations/lifestyleCalculations';
import { formatCurrency } from '../utils/formatters';

interface LifestyleCalculatorProps {
  onBack: () => void;
  initialMonthlyIncome?: number;
}

const LifestyleCalculator: React.FC<LifestyleCalculatorProps> = ({ 
  onBack, 
  initialMonthlyIncome = 50000 
}) => {
  // Estados principales
  const [selectedCity, setSelectedCity] = useState<City>(cities[0]);
  const [monthlyIncome, setMonthlyIncome] = useState(initialMonthlyIncome);
  const [selectedExpenses, setSelectedExpenses] = useState<Record<string, SelectedExpense>>({});
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [activeTab, setActiveTab] = useState<'configuration' | 'analysis' | 'comparison'>('configuration');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Actualizar ingreso inicial cuando cambie el prop
  useEffect(() => {
    setMonthlyIncome(initialMonthlyIncome);
  }, [initialMonthlyIncome]);

  // Calcular gastos totales cuando cambien las selecciones
  useEffect(() => {
    const total = calculateTotalExpenses(selectedExpenses, expenseCategories, selectedCity);
    setTotalExpenses(total);
  }, [selectedExpenses, selectedCity]);

  // Manejar cambios en gastos
  const handleExpenseChange = (expenseId: string, quantity: number, customAmount?: number) => {
    setSelectedExpenses(prev => ({
      ...prev,
      [expenseId]: { quantity, customAmount }
    }));
  };

  // Remover gasto seleccionado
  const handleRemoveExpense = (expenseId: string) => {
    setSelectedExpenses(prev => {
      const newExpenses = { ...prev };
      delete newExpenses[expenseId];
      return newExpenses;
    });
  };

  // Get icon for expense category
  const getCategoryIcon = (iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'Home': <Home className="w-5 h-5" />,
      'Car': <Car className="w-5 h-5" />,
      'Utensils': <Utensils className="w-5 h-5" />,
      'Music': <Music className="w-5 h-5" />,
      'Users': <Users className="w-5 h-5" />,
      'GraduationCap': <GraduationCap className="w-5 h-5" />,
      'Stethoscope': <Stethoscope className="w-5 h-5" />,
      'Wrench': <Wrench className="w-5 h-5" />,
      'Plane': <Plane className="w-5 h-5" />
    };
    return iconMap[iconName] || <DollarSign className="w-5 h-5" />;
  };

  // Calcular métricas de asequibilidad
  const affordabilityMetrics = calculateAffordabilityMetrics(monthlyIncome, totalExpenses);
  
  // Generar recomendaciones
  const recommendations = generateRecommendations(affordabilityMetrics, selectedCity, monthlyIncome);

  // Calcular estadísticas por categoría
  const expensesByCategory = expenseCategories.map(category => {
    const categoryExpenses = Object.entries(selectedExpenses)
      .filter(([expenseId]) => {
        const expense = category.items.find(item => item.id === expenseId);
        return !!expense;
      })
      .reduce((total, [expenseId, config]) => {
        const expense = category.items.find(item => item.id === expenseId);
        if (expense) {
          const adjustedCost = calculateAdjustedCost(expense.baseCost, selectedCity.costOfLiving);
          const cost = config.customAmount !== undefined ? 
            config.customAmount : 
            adjustedCost * config.quantity;
          return total + cost;
        }
        return total;
      }, 0);

    return {
      category: category.name,
      icon: category.icon,
      total: categoryExpenses,
      percentage: totalExpenses > 0 ? (categoryExpenses / totalExpenses) * 100 : 0
    };
  }).filter(item => item.total > 0);

  // Calcular certificados necesarios
  const certificateMonthlyIncome = 10000; // Ingreso promedio por certificado (ajustable)
  const certificatesNeeded = calculateCertificatesForLifestyle(totalExpenses, certificateMonthlyIncome);

  // Generar reporte
  const generateReport = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Análisis de Estilo de Vida - ${selectedCity.name}</title>
        <style>
          body {
            font-family: system-ui, -apple-system, sans-serif;
            line-height: 1.6;
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #1f2937;
            background: #ffffff;
          }
          .header {
            text-align: center;
            margin-bottom: 60px;
            padding-bottom: 30px;
            border-bottom: 3px solid #16a34a;
          }
          .header h1 {
            color: #16a34a;
            font-size: 2.5em;
            margin-bottom: 10px;
          }
          .section {
            margin-bottom: 40px;
            padding: 30px;
            background: #f3f4f6;
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          .stat-card {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            background: white;
            border-radius: 8px;
            overflow: hidden;
          }
          th, td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
          }
          th {
            background: #16a34a;
            color: white;
            font-weight: 500;
          }
          tr:nth-child(even) {
            background: #f9fafb;
          }
          .footer {
            text-align: center;
            margin-top: 60px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            color: #374151;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Análisis de Estilo de Vida</h1>
          <h2>${selectedCity.name}, ${selectedCity.country}</h2>
          <p>Generado el ${formattedDate}</p>
        </div>
        
        <div class="section">
          <h2>Resumen Financiero</h2>
          <div class="grid">
            <div class="stat-card">
              <h3>Ingreso Mensual</h3>
              <p>${formatCurrency(monthlyIncome, 'MXN')}</p>
            </div>
            <div class="stat-card">
              <h3>Gastos Totales</h3>
              <p>${formatCurrency(totalExpenses, 'MXN')}</p>
            </div>
            <div class="stat-card">
              <h3>Dinero Restante</h3>
              <p>${formatCurrency(affordabilityMetrics.remainingIncome, 'MXN')}</p>
            </div>
            <div class="stat-card">
              <h3>Nivel de Asequibilidad</h3>
              <p>${affordabilityMetrics.affordabilityPercentage.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>Desglose de Gastos por Categoría</h2>
          <table>
            <thead>
              <tr>
                <th>Categoría</th>
                <th>Monto Mensual</th>
                <th>Porcentaje del Total</th>
              </tr>
            </thead>
            <tbody>
              ${expensesByCategory.map(item => `
                <tr>
                  <td>${item.category}</td>
                  <td>${formatCurrency(item.total, 'MXN')}</td>
                  <td>${item.percentage.toFixed(1)}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2>Información de la Ciudad</h2>
          <div class="grid">
            <div class="stat-card">
              <h3>Índice de Costo de Vida</h3>
              <p>${selectedCity.costOfLiving}</p>
            </div>
            <div class="stat-card">
              <h3>Índice de Renta</h3>
              <p>${selectedCity.rentIndex}</p>
            </div>
            <div class="stat-card">
              <h3>Índice de Comestibles</h3>
              <p>${selectedCity.groceriesIndex}</p>
            </div>
            <div class="stat-card">
              <h3>Índice de Restaurantes</h3>
              <p>${selectedCity.restaurantIndex}</p>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>Recomendaciones</h2>
          <ul>
            ${recommendations.map(rec => `<li>${rec}</li>`).join('')}
          </ul>
        </div>

        <div class="footer">
          <p>© ${new Date().getFullYear()} Cosecha Capital</p>
          <p>Análisis de estilo de vida basado en índices de costo de vida internacionales.</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Analisis_Estilo_Vida_${selectedCity.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Certificado para tu Estilo de Vida</h1>
                <p className="text-gray-600">Descubre qué estilo de vida podrías mantener en diferentes ciudades del mundo</p>
                
                {/* Disclaimer */}
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('configuration')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'configuration'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Target className="w-5 h-5" />
              <span className="font-medium">Configuración</span>
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'analysis'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              <span className="font-medium">Análisis</span>
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'comparison'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <PieChart className="w-5 h-5" />
              <span className="font-medium">Comparación</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'configuration' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Income Configuration */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Tu Ingreso Mensual</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ingreso Mensual Disponible
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Ingreso mensual"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-center p-6 bg-green-50 rounded-xl">
                    <div className="text-3xl font-bold text-green-700 mb-1">
                      {formatCurrency(monthlyIncome, 'MXN')}
                    </div>
                    <div className="text-sm text-green-600">mensuales disponibles</div>
                  </div>
                </div>
              </div>
            </div>

            {/* City Selection */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Selecciona una Ciudad</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cities.map((city) => (
                  <button
                    key={city.id}
                    onClick={() => setSelectedCity(city)}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                      selectedCity.id === city.id
                        ? 'border-blue-500 bg-blue-50 shadow-md transform scale-105'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-4xl mb-3">{city.icon}</div>
                      <h3 className="font-medium text-gray-800">{city.name}</h3>
                      <p className="text-sm text-gray-500">{city.country}</p>
                      <div className="mt-2 text-xs text-blue-600">
                        Índice: {city.costOfLiving}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Selected City Info */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-5xl">{selectedCity.icon}</div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-800">{selectedCity.name}</h2>
                  <p className="text-blue-600">{selectedCity.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Costo de Vida</div>
                  <div className="text-xl font-bold text-blue-700">{selectedCity.costOfLiving}</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Renta</div>
                  <div className="text-xl font-bold text-purple-700">{selectedCity.rentIndex}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Comida</div>
                  <div className="text-xl font-bold text-green-700">{selectedCity.groceriesIndex}</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Restaurantes</div>
                  <div className="text-xl font-bold text-orange-700">{selectedCity.restaurantIndex}</div>
                </div>
              </div>
            </div>

            {/* Expense Categories */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Configura tu Estilo de Vida</h2>
              </div>
              
              <div className="space-y-6">
                {expenseCategories.map((category) => (
                  <div key={category.id} className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      {getCategoryIcon(category.icon)}
                      <h3 className="text-lg font-semibold text-gray-800">{category.name}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {category.items.map((item) => {
                        const adjustedCost = calculateAdjustedCost(item.baseCost, selectedCity.costOfLiving);
                        const isSelected = selectedExpenses[item.id];
                        
                        return (
                          <div
                            key={item.id}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-800 text-sm">{item.name}</h4>
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleExpenseChange(item.id, item.default_value || 1);
                                  } else {
                                    handleRemoveExpense(item.id);
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              {formatCurrency(adjustedCost, 'MXN')}
                              {item.input_type === 'quantity' && ' por unidad'}
                            </div>
                            {isSelected && item.input_type === 'quantity' && (
                              <div className="mt-2">
                                <label className="block text-xs text-gray-500 mb-1">Cantidad</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={isSelected.quantity}
                                  onChange={(e) => handleExpenseChange(item.id, parseInt(e.target.value) || 1)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            )}
                            {isSelected && item.input_type === 'custom_amount' && (
                              <div className="mt-2">
                                <label className="block text-xs text-gray-500 mb-1">Monto personalizado</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={isSelected.customAmount || adjustedCost}
                                  onChange={(e) => handleExpenseChange(item.id, 1, parseInt(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'analysis' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Results Summary */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    Análisis de Estilo de Vida en {selectedCity.name}
                  </h2>
                </div>
                <button
                  onClick={generateReport}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  <span>Descargar Reporte</span>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                  <div className="text-sm text-gray-600 mb-1">Ingreso Mensual</div>
                  <div className="text-2xl font-bold text-green-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={monthlyIncome}
                      currencyFormat="MXN"
                      duration={1.2}
                      staggerDelay={0.08}
                    />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                  <div className="text-sm text-gray-600 mb-1">Gastos Totales</div>
                  <div className="text-2xl font-bold text-blue-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={totalExpenses}
                      currencyFormat="MXN"
                      duration={1.2}
                      staggerDelay={0.08}
                    />
                  </div>
                </div>
                
                <div className={`bg-gradient-to-br p-4 rounded-xl ${
                  affordabilityMetrics.remainingIncome >= 0 
                    ? 'from-green-50 to-green-100' 
                    : 'from-red-50 to-red-100'
                }`}>
                  <div className="text-sm text-gray-600 mb-1">Dinero Restante</div>
                  <div className={`text-2xl font-bold relative overflow-hidden ${
                    affordabilityMetrics.remainingIncome >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    <AnimatedNumberDisplay
                      value={affordabilityMetrics.remainingIncome}
                      currencyFormat="MXN"
                      duration={1.2}
                      staggerDelay={0.08}
                    />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                  <div className="text-sm text-gray-600 mb-1">Certificados Necesarios</div>
                  <div className="text-2xl font-bold text-purple-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={certificatesNeeded}
                      currencyFormat="MXN"
                      duration={1.2}
                      staggerDelay={0.08}
                      isCurrency={false}
                    />
                  </div>
                </div>
              </div>

              {/* Affordability Indicator */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Nivel de Asequibilidad</span>
                  <span className="text-sm font-medium text-gray-700">{affordabilityMetrics.affordabilityPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 ${
                      affordabilityMetrics.affordabilityPercentage <= 70 ? 'bg-green-500' :
                      affordabilityMetrics.affordabilityPercentage <= 90 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, affordabilityMetrics.affordabilityPercentage)}%` }}
                  ></div>
                </div>
                <div className="mt-2 text-center">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    affordabilityMetrics.affordabilityLevel === 'excellent' ? 'bg-green-100 text-green-800' :
                    affordabilityMetrics.affordabilityLevel === 'good' ? 'bg-blue-100 text-blue-800' :
                    affordabilityMetrics.affordabilityLevel === 'tight' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {affordabilityMetrics.affordabilityMessage}
                  </span>
                </div>
              </div>

              {/* Recommendations */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-3">Recomendaciones</h3>
                <div className="space-y-2">
                  {recommendations.map((recommendation, index) => (
                    <p key={index} className="text-sm text-blue-700">
                      • {recommendation}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'comparison' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Expenses by Category */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Desglose de Gastos por Categoría</h2>
              
              {expensesByCategory.length > 0 ? (
                <div className="space-y-4">
                  {expensesByCategory.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {getCategoryIcon(item.icon)}
                        <span className="font-medium text-gray-800">{item.category}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          <AnimatedNumberDisplay
                            value={item.total}
                            currencyFormat="MXN"
                            duration={1.0}
                            staggerDelay={0.06}
                          />
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.percentage.toFixed(1)}% del total
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-2">No has seleccionado gastos aún</div>
                  <button
                    onClick={() => setActiveTab('configuration')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Configurar Gastos
                  </button>
                </div>
              )}
            </div>

            {/* City Comparison */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Comparación con Otras Ciudades</h2>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ciudad
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gastos Ajustados
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dinero Restante
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Asequibilidad
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cities.map((city) => {
                      const adjustedExpenses = calculateTotalExpenses(selectedExpenses, expenseCategories, city);
                      const cityAffordability = calculateAffordabilityMetrics(monthlyIncome, adjustedExpenses);
                      
                      return (
                        <tr 
                          key={city.id} 
                          className={`hover:bg-gray-50 ${city.id === selectedCity.id ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{city.icon}</span>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{city.name}</div>
                                <div className="text-sm text-gray-500">{city.country}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                            <AnimatedNumberDisplay
                              value={adjustedExpenses}
                              currencyFormat="MXN"
                              duration={1.0}
                              staggerDelay={0.06}
                            />
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${
                            cityAffordability.remainingIncome >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            <AnimatedNumberDisplay
                              value={cityAffordability.remainingIncome}
                              currencyFormat="MXN"
                              duration={1.0}
                              staggerDelay={0.06}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              cityAffordability.affordabilityLevel === 'excellent' ? 'bg-green-100 text-green-800' :
                              cityAffordability.affordabilityLevel === 'good' ? 'bg-blue-100 text-blue-800' :
                              cityAffordability.affordabilityLevel === 'tight' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {cityAffordability.affordabilityPercentage.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LifestyleCalculator;